import { Button, Modal } from '@mairistem/composants';
import React from 'react';
import useModalSuperUserValidation from '../../../../../hooks/useModalSuperUserValidation';
import useTraitementLong from '../../../../../hooks/useTraitementLong';
import type { BordereauWithState } from '../../../../../interfaces/pieces/TreeRow';
import {
	denumeroterBordereau,
	getBordereau,
} from '../../../../../services/piecesComptables/bordereaux/bordereaux';
import { afficherInfoChorusDuJob } from '../../../../../utils/chorus/afficherInfoChorus';

interface BordereauDenumeroterModalProps {
	/** Bordereau dénuméroté, relu avec ses journaux, une fois le job terminé. */
	onChange: (data: BordereauWithState) => void;
	onClose: VoidFunction;
	bordereau: BordereauWithState;
	visible: boolean;
	isSuperUser: boolean;
	isArchive: boolean;
}

const BordereauDenumeroterModal = ({
	onChange,
	onClose,
	bordereau,
	visible,
	isSuperUser,
	isArchive,
}: BordereauDenumeroterModalProps) => {
	const { isValidated, validationComponent } = useModalSuperUserValidation();
	// dénumérotation faite par un job : à sa fin, bordereau relu avec ses journaux, et information sur ses mandats issus
	// de factures Chorus
	const { isEnCours, lancer, traitementComponent, fermeture } =
		useTraitementLong(
			isArchive
				? "Annulation de l'archivage du bordereau de rejet"
				: 'Dénumérotation du bordereau',
			'Erreur lors de la dénumérotation du bordereau.',
			async (job) => {
				afficherInfoChorusDuJob(job);
				const bordereauDenumerote = await getBordereau(
					bordereau.identifiant,
					new URLSearchParams({ withJournaux: 'true' }),
				);
				onChange(bordereauDenumerote as BordereauWithState);
				onClose();
			},
		);

	return (
		<Modal
			{...fermeture(onClose)}
			open={visible}
			data-testid="bordereauDenumeroterModal"
		>
			<Modal.Header>
				{isArchive
					? `Annuler l'archivage de ce bordereau de rejet n°${bordereau.numero}`
					: `Dénuméroter le bordereau n°${bordereau.numero}`}
			</Modal.Header>
			<Modal.Content>
				<p>
					Cette action repositionne le bordereau dans les en-cours de saisie. Le
					bordereau perd sa numérotation définitive en vue de pouvoir remanier
					son contenu (ajout, modification ou suppression d&apos;écritures)
				</p>
				{isSuperUser && validationComponent}
				{traitementComponent}
			</Modal.Content>
			<Modal.Actions>
				<Button
					onClick={onClose}
					disabled={isEnCours}
					data-testid="bordereauDenumeroterModalCancel"
				>
					Annuler
				</Button>
				<Button
					disabled={isEnCours || (isSuperUser && !isValidated)}
					primary
					onClick={() =>
						lancer(() => denumeroterBordereau(bordereau.identifiant))
					}
					data-testid="bordereauDenumeroterModalConfirm"
				>
					Confirmer
				</Button>
			</Modal.Actions>
		</Modal>
	);
};

export default BordereauDenumeroterModal;
